import { Suspense, lazy, useEffect, useMemo, type ComponentType } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
import { Sidebar } from '../components/Sidebar';
import { CookieNotice } from '../components/CookieNotice';
import { useClock } from '../hooks/useClock';
import { usePersistentState } from '../hooks/usePersistentState';
import {
  detectPreferredLocale,
  detectPreferredToolId,
  rememberLocale,
  rememberToolId,
  setDocumentMetadata,
} from '../lib/app';
import { createStorageKey } from '../lib/storage';
import { AboutPage, PrivacyPage } from '../pages/StaticPages';
import {
  getToolById,
  isStaticPageId,
  isSupportedLocale,
  isToolId,
  toolRegistry,
  type AppLocale,
  type ToolId,
} from './config';
const PasswordGeneratorTool = lazy(async () => {
  const module = await import('../tools/BasicTools');
  return { default: module.PasswordGeneratorTool };
});

const CharacterCounterTool = lazy(async () => {
  const module = await import('../tools/BasicTools');
  return { default: module.CharacterCounterTool };
});

const HtmlSpacesTool = lazy(async () => {
  const module = await import('../tools/BasicTools');
  return { default: module.HtmlSpacesTool };
});

const EncodingConverterTool = lazy(async () => {
  const module = await import('../tools/BasicTools');
  return { default: module.EncodingConverterTool };
});

const Base64Tool = lazy(async () => {
  const module = await import('../tools/BasicTools');
  return { default: module.Base64Tool };
});

const UrlTool = lazy(async () => {
  const module = await import('../tools/BasicTools');
  return { default: module.UrlTool };
});

const HtmlEntitiesTool = lazy(async () => {
  const module = await import('../tools/BasicTools');
  return { default: module.HtmlEntitiesTool };
});

const IphoneCountdownTool = lazy(async () => {
  const module = await import('../tools/BasicTools');
  return { default: module.IphoneCountdownTool };
});

const JsonFormatterTool = lazy(() =>
  import('../tools/JsonFormatterTool').then(module => ({
    default: module.JsonFormatterTool,
  }))
);
const ColorConverterTool = lazy(() =>
  import('../tools/ColorConverterTool').then(module => ({
    default: module.ColorConverterTool,
  }))
);
const PomodoroTimerTool = lazy(() =>
  import('../tools/PomodoroTimerTool').then(module => ({
    default: module.PomodoroTimerTool,
  }))
);

const toolComponents: Record<ToolId, ReturnType<typeof lazy<ComponentType>>> = {
  'password-generator': PasswordGeneratorTool,
  'char-counter': CharacterCounterTool,
  'special-space': HtmlSpacesTool,
  'encoding-converter': EncodingConverterTool,
  base64: Base64Tool,
  'json-formatter': JsonFormatterTool,
  'color-converter': ColorConverterTool,
  'url-encoder': UrlTool,
  'html-entities': HtmlEntitiesTool,
  'iphone-countdown': IphoneCountdownTool,
  pomodoro: PomodoroTimerTool,
};

const searchQueryStorageKey = createStorageKey('app.searchQuery');
const sidebarOpenStorageKey = createStorageKey('app.sidebarOpen');

function PreferredRedirect() {
  return (
    <Navigate
      replace
      to={`/${detectPreferredLocale()}/${detectPreferredToolId()}`}
    />
  );
}

function LocaleToolRoute() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { locale: routeLocale, toolId: routeToolId } = useParams();
  const [searchQuery, setSearchQuery] = usePersistentState(
    searchQueryStorageKey,
    ''
  );
  const [sidebarOpen, setSidebarOpen] = usePersistentState(
    sidebarOpenStorageKey,
    typeof window === 'undefined' ? true : window.innerWidth > 1100
  );

  if (!isSupportedLocale(routeLocale)) {
    return (
      <Navigate
        replace
        to={`/${detectPreferredLocale()}/${detectPreferredToolId()}`}
      />
    );
  }

  if (routeToolId && !isToolId(routeToolId) && !isStaticPageId(routeToolId)) {
    return (
      <Navigate replace to={`/${routeLocale}/${detectPreferredToolId()}`} />
    );
  }

  if (!routeToolId) {
    return (
      <Navigate replace to={`/${routeLocale}/${detectPreferredToolId()}`} />
    );
  }

  const locale = routeLocale as AppLocale;
  const pageId = isStaticPageId(routeToolId) ? routeToolId : null;
  const toolId: ToolId | null =
    !pageId && isToolId(routeToolId) ? routeToolId : null;
  const activeTool = toolId ? getToolById(toolId) : null;
  const ActiveToolComponent = toolId ? toolComponents[toolId] : null;
  const { dayLabel, timeLabel } = useClock(locale);

  useEffect(() => {
    void i18n.changeLanguage(locale);
    rememberLocale(locale);
  }, [i18n, locale]);

  useEffect(() => {
    if (toolId) {
      rememberToolId(toolId);
    }
  }, [toolId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pageId, toolId]);

  useEffect(() => {
    if (window.innerWidth > 1100) {
      setSidebarOpen(true);
      return;
    }

    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1100) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const filteredTools = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return [...toolRegistry];
    }

    return toolRegistry.filter(tool => {
      const name = t(
        `navigation.tools.${tool.translationKey}.name`
      ).toLowerCase();
      const description = t(
        `navigation.tools.${tool.translationKey}.description`
      ).toLowerCase();
      return (
        name.includes(normalizedQuery) || description.includes(normalizedQuery)
      );
    });
  }, [searchQuery, t]);

  useEffect(() => {
    if (pageId && !activeTool) {
      setDocumentMetadata({
        locale,
        title: t(`pages.${pageId}.metaTitle`),
        description: t(`pages.${pageId}.metaDescription`),
        keywords: t('seo.siteKeywords'),
      });
      return;
    }

    if (!activeTool) {
      return;
    }

    const toolName = t(`navigation.tools.${activeTool.translationKey}.name`);
    const toolDescription = t(
      `navigation.tools.${activeTool.translationKey}.description`
    );
    setDocumentMetadata({
      locale,
      title: `${toolName} | ${t('seo.siteTitle')}`,
      description: toolDescription,
      keywords: t('seo.siteKeywords'),
    });
  }, [activeTool, locale, pageId, t]);

  function handleLocaleChange(nextLocale: AppLocale) {
    navigate(`/${nextLocale}/${pageId ?? toolId ?? detectPreferredToolId()}`);
  }

  function handleCloseSidebar() {
    if (window.innerWidth <= 1100) {
      setSidebarOpen(false);
    }
  }

  return (
    <div
      className={`app-shell with-sidebar${sidebarOpen ? ' sidebar-open' : ''}`}
    >
      <div
        className='sidebar-overlay'
        onClick={handleCloseSidebar}
        role='presentation'
      />
      <Sidebar
        currentLocale={locale}
        isOpen={sidebarOpen}
        tools={filteredTools}
        onClose={handleCloseSidebar}
      />
      <Header
        currentLocale={locale}
        dayLabel={dayLabel}
        searchQuery={searchQuery}
        timeLabel={timeLabel}
        onLocaleChange={handleLocaleChange}
        onOpenSidebar={() => setSidebarOpen(true)}
        onSearchChange={setSearchQuery}
      />
      <main className='main'>
        <Suspense
          fallback={
            <section className='tool-section active'>
              <div className='card'>{t('common.loading')}</div>
            </section>
          }
        >
          {pageId === 'about' ? <AboutPage /> : null}
          {pageId === 'privacy' ? <PrivacyPage /> : null}
          {ActiveToolComponent ? <ActiveToolComponent /> : null}
        </Suspense>
      </main>
      <Footer currentLocale={locale} />
      <CookieNotice currentLocale={locale} />
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path='/' element={<PreferredRedirect />} />
      <Route path='/:locale' element={<LocaleToolRoute />} />
      <Route path='/:locale/:toolId' element={<LocaleToolRoute />} />
      <Route path='*' element={<PreferredRedirect />} />
    </Routes>
  );
}
